/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-explicit-any */

/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import React, { useCallback, useRef, useState } from 'react';
import { ThemeProvider } from '@oneaudi/unified-web-common';
import { Headline, Icon, Layout, TextLink } from '@oneaudi/unified-web-components';
import { FootnoteContextProvider, useInViewEffect } from '@oneaudi/feature-app-utils';
import type { UnifiedThemeType } from '@oneaudi/unified-web-common';
import type { VueFormatterServiceInterfaceV1 } from '../feature-service-types';
import { renderTextWithFootnotesReferencesV2 } from '../feature-service-types';
import { UeElement } from '@oneaudi/falcon-tools';
import type { EditorialTextProps } from '../../../@types/types';
import { processBodyText } from './processBodyText';
import { getValidCtaAriaLabel } from '../../utils/ariaLabelHelper';
import { getThemeName } from '../../utils/alphaHelper';
import type { ThemeName } from '@oneaudi/unified-web-alpha-common/dist/cjs/components/theme-provider/theme';
import {
  ContainerDiv,
  ButtonContainerDiv,
  TextWrapperDiv,
  TextLayout,
  StyledTextLayoutItem,
} from './styledComponents';
import { ConsumptionAndEmission } from './ConsumptionAndEmission';
import Paragraphs from './Paragraphs';
import { useEditorialAnchors } from '../../hooks/useEditorialAnchors';
import SitewideSearch from './SitewideSearch';
import { IconWrapper } from './SitewideSearch/SidewideSearch.styles';

export const EditorialText = ({
  content,
  footnoteReferenceService,
  vueFormatterService,
  localeService,
  trackingService,
  showSearch,
}: EditorialTextProps) => {
  const eTextRef = useRef<HTMLDivElement | null>(null);
  const eTextContent = useRef<HTMLDivElement | null>(null);
  const {
    bodyText,
    theme,
    justification,
    ctaButtonLabel,
    ctaButtonUrl,
    ctaButtonOpenInNewTab,
    ariaLabel,
    consuptionAndEmission,
    disclaimer,
    bottomMargin,
    reduceTextWidth,
    paragraphs,
    headline,
    headlineOrder,
    headlineTag,
    sortBy,
    showPrice,
  } = content;

  const [sitewideSearchOpen, setSidewideSearchOpen] = useState(false);

  const themeActive = getThemeName((theme || 'light') as ThemeName);
  let processedCopy;

  if (bodyText) {
    processedCopy = processBodyText(bodyText as string);
  }

  const getFirstLineFromBodyText = (text: string): string => {
    try {
      // Create a temporary DOM element to safely extract text content
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = text;
      const cleanText = tempDiv.textContent || tempDiv.innerText || '';

      // Split by line breaks and take the first non-empty line
      const lines = cleanText.split(/\r?\n|\r/);
      const firstLine = lines.find((line) => line.trim().length > 0);

      return firstLine ? firstLine.trim() : '';
    } catch {
      return '';
    }
  };

  const getFirstElementInnerText = useCallback((): string => {
    try {
      let firstText = '';

      // Check if eTextContent exists and has current ref
      if (!eTextContent || !eTextContent.current) {
        // Fallback: use first line of bodyText if no DOM content
        return getFirstLineFromBodyText((bodyText as string) || '');
      }

      // Check if there's any content at all
      if (!eTextContent.current.firstChild) {
        // Fallback: use first line of bodyText if no DOM content
        return getFirstLineFromBodyText((bodyText as string) || '');
      }

      // Look for title elements (h1, h2, h3, h4, h5, h6) first
      const titleElements = eTextContent.current.querySelectorAll('h1, h2, h3, h4, h5, h6');
      if (titleElements.length > 0) {
        const firstTitle = titleElements[0] as HTMLElement;
        firstText = firstTitle.innerText || firstTitle.textContent || '';
        if (firstText.trim()) {
          return firstText.trim();
        }
      }

      // Case 1: Direct element (non-DIV)
      const firstChild = eTextContent.current.firstChild as HTMLElement;
      if (firstChild && firstChild.tagName && firstChild.tagName !== 'DIV') {
        firstText = firstChild.innerText || '';
        if (firstText.trim()) {
          return firstText.trim();
        }
      }

      // Case 2: Nested structure - check each level safely
      const level1 = eTextContent.current.firstChild;
      if (!level1) {
        return getFirstLineFromBodyText((bodyText as string) || '');
      }

      const level2 = level1.firstChild;
      if (!level2) {
        return getFirstLineFromBodyText((bodyText as string) || '');
      }

      const level3 = level2.firstChild;
      if (!level3) {
        return getFirstLineFromBodyText((bodyText as string) || '');
      }

      // Safely get innerHTML from the nested element
      const nestedElement = level3 as HTMLElement;
      if (nestedElement && nestedElement.innerHTML !== undefined) {
        firstText = nestedElement.innerHTML;
        // Clean HTML from the result using safe DOM parsing
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = firstText;
        const cleanText = tempDiv.textContent || tempDiv.innerText || '';
        if (cleanText.trim()) {
          return cleanText.trim();
        }
      }

      // Final fallback: use first line of bodyText
      return getFirstLineFromBodyText((bodyText as string) || '');
    } catch {
      // Last resort fallback
      return getFirstLineFromBodyText((bodyText as string) || '');
    }
  }, [bodyText]);

  const getUrlExtension = (url: string) => {
    return url?.split(/[#?]/)[0].split('.')?.pop()?.trim();
  };

  const setEventAction = useCallback((ctaUrl: string) => {
    const isExternalURL = (url: string) => new URL(url).origin !== window.location.origin;
    if (getUrlExtension(ctaUrl) === 'pdf' || getUrlExtension(ctaUrl) === 'xlsx') return 'download';

    if (ctaUrl === '') return 'content';

    if (isExternalURL(ctaUrl)) return 'exit_link';

    return 'internal_link';
  }, []);

  const isFullUrl = (url: string) => {
    const expression = /(www|http:|https:)+[^\s]+[\w]/gi;
    const regex = new RegExp(expression);

    if (url.match(regex)) {
      return url;
    }

    const newUrl = new URL(url, window.location.href);
    return newUrl.href;
  };

  const addTrackingClickEvent = useCallback(
    (cta: HTMLAnchorElement) => {
      const url = cta.href;

      const dataLayerEvent: any = {
        eventInfo: {
          eventAction: setEventAction(url),
          eventName: `editorial text - click ${setEventAction(url) === 'download' ? 'to download' : 'on text link'}`,
        },
        attributes: {
          componentName: 'editorial-text',
          label: cta.innerText,
          targetURL: isFullUrl(url),
          clickID: '',
          elementName: 'text link',
          value: cta.parentElement?.previousElementSibling?.innerHTML
            ? cta.parentElement?.previousElementSibling?.innerHTML
            : getFirstElementInnerText(),
          pos: '',
          relatedElements:
            getUrlExtension(url) === 'pdf' || getUrlExtension(url) === 'xlsx'
              ? [
                  {
                    elementName: `${getUrlExtension(url)} file`,
                    elementValue: url
                      .split('/')
                      .pop()
                      ?.replace(/.pdf|.xlsx/gi, ''),
                  },
                ]
              : [cta.innerHTML],
        },
      };

      cta.onclick = () => {
        trackingService?.track({
          event: dataLayerEvent,
        });
      };
    },
    [getFirstElementInnerText, setEventAction, trackingService],
  );

  useInViewEffect(() => {
    const readyEvent = {
      event: {
        attributes: {
          componentName: 'editorial-text',
          elementName: '',
          label: '',
          clickID: '',
          pos: '',
          targetURL: '',
          value: getFirstElementInnerText(),
        },
        eventInfo: {
          eventAction: 'impression',
          eventName: `editorial text - impression`,
        },
      },
    };
    trackingService?.track(readyEvent);
  });

  const addTrackingClickEventCallback = React.useCallback(
    (cta: HTMLAnchorElement) => {
      addTrackingClickEvent(cta);
    },
    [addTrackingClickEvent],
  );

  useEditorialAnchors(eTextRef, addTrackingClickEventCallback);

  return (
    <div data-theme-name={themeActive}>
      <FootnoteContextProvider footnoteReferenceService={footnoteReferenceService}>
        <ThemeProvider theme={{ name: themeActive } as UnifiedThemeType}>
          <ContainerDiv
            justification={justification}
            bottomMargin={bottomMargin}
            reduceTextWidth={reduceTextWidth}
            ref={eTextRef}
            data-testid="container"
            hasBodyText={Boolean(bodyText)}
          >
            <TextLayout reduceTextWidth={reduceTextWidth} theme={themeActive}>
              <Layout data-test-id="title" direction="column">
                <StyledTextLayoutItem basis="100%">
                  <div>
                    {headline && (
                      <Headline
                        variant={{ type: 'headline', order: headlineOrder, weight: 'normal' }}
                        tag={headlineTag}
                        className="edirotial-text-headline"
                        data-testid="main-headline"
                      >
                        {renderTextWithFootnotesReferencesV2(headline as string)}
                      </Headline>
                    )}
                    {bodyText && (
                      <TextWrapperDiv
                        data-testid="textWrapper"
                        align={justification}
                        theme={themeActive}
                        ref={eTextContent}
                      >
                        {processedCopy}
                      </TextWrapperDiv>
                    )}
                  </div>
                </StyledTextLayoutItem>
                {bodyText &&
                  ctaButtonUrl &&
                  ctaButtonLabel &&
                  ctaButtonUrl?.length > 0 &&
                  ctaButtonLabel?.length > 0 && (
                    <StyledTextLayoutItem basis="auto">
                      <ButtonContainerDiv align={justification}>
                        <TextLink
                          variant="standalone"
                          size="md"
                          target={ctaButtonOpenInNewTab === 'newTab' ? '_blank' : '_self'}
                          href={ctaButtonUrl}
                          onClick={() => {}}
                          icon="forward"
                          iconPosition="trailing"
                          aria-label={getValidCtaAriaLabel(
                            ariaLabel as string,
                            ctaButtonLabel as string,
                          )}
                          data-testid="main-cta"
                        >
                          <UeElement type="text" property="ctaButtonLabel" label="CTA Label">
                            {ctaButtonLabel}
                          </UeElement>
                        </TextLink>
                      </ButtonContainerDiv>
                    </StyledTextLayoutItem>
                  )}

                <TextLayout reduceTextWidth={reduceTextWidth} theme={themeActive}>
                  <Layout data-test-id="title-paragraph" direction="column">
                    <StyledTextLayoutItem basis="100%">
                      {paragraphs && paragraphs.length > 0 && (
                        <Paragraphs
                          eTextContent={eTextContent}
                          paragraphs={paragraphs}
                          justification={justification}
                          themeActive={themeActive}
                        />
                      )}
                    </StyledTextLayoutItem>
                  </Layout>
                </TextLayout>

                {consuptionAndEmission?.length > 0 || disclaimer?.length > 0 ? (
                  <ConsumptionAndEmission
                    consuptionAndEmission={consuptionAndEmission}
                    disclaimer={disclaimer}
                    theme={themeActive}
                    vueFormatterService={vueFormatterService as VueFormatterServiceInterfaceV1}
                    align={justification}
                    localeService={localeService!}
                  />
                ) : null}
              </Layout>
            </TextLayout>
            {showSearch && (
              <IconWrapper
                onClick={() => {
                  setSidewideSearchOpen(true);
                }}
                data-testid="sitewide-open-icon"
              >
                <Icon name="search" size="s" />
              </IconWrapper>
            )}
            {sitewideSearchOpen && (
              <SitewideSearch
                setSidewideSearchOpen={setSidewideSearchOpen}
                localeService={localeService}
                sortBy={sortBy}
                showPrice={showPrice}
              />
            )}
          </ContainerDiv>
        </ThemeProvider>
      </FootnoteContextProvider>
    </div>
  );
};
